import {
    Combobox,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxInput,
    ComboboxItem,
    ComboboxList,
} from 'components/ui/combobox';
import React, { useEffect } from 'react';
import { t } from 'utils/getTranslations.ts';
import { getSelectorKey } from '../../getSelectorKey.ts';
import { DropdownFilterForm, MainFilterForm, QuickFilterForm, SelectOption } from '../../ListTableTypes.tsx';
import { FilterLabel } from './FilterLabel';

const getOptionLabel = (option: SelectOption) => option.label.toString();

export const Dropdown: React.FC<{
    filter: DropdownFilterForm;
    quickFilters: QuickFilterForm;
    mainFilters: MainFilterForm;
    onChange: (value: SelectOption | null, groupSelector: string) => void;
    /** Whether the field offers its clear ×: off for a dropdown that always holds an option. */
    clearable?: boolean;
}> = React.memo(({ filter, onChange, quickFilters, mainFilters, clearable = true }) => {
    const isHidden =
        filter.getShouldHide !== undefined
            ? filter.getShouldHide({
                  mainFilters,
                  quickFilters,
                  groupSelector: getSelectorKey(filter),
              })
            : false;

    useEffect(() => {
        // Only when there is something to drop: `onChange` re-renders the whole drawer, so clearing an
        // already-empty filter on every render of a hidden one would loop.
        if (isHidden && !filter?.preserveValueWhenHidden && filter.selected != null) {
            onChange(null, getSelectorKey(filter));
        }
    }, [filter, isHidden, onChange]);

    // The selected option is left out of the list: the field already shows it.
    const options = (filter?.options || []).filter((option) => option.value !== filter?.selected?.value);

    if (isHidden) {
        return null;
    }
    return (
        <div className="flex grow flex-col gap-2" key={`filter-${filter.selector}`}>
            {filter.label && (
                <FilterLabel label={filter.label} tooltip={filter.tooltip} tooltipValues={filter.tooltipValues} />
            )}
            <Combobox<SelectOption>
                items={options}
                value={filter?.selected ?? null}
                itemToStringLabel={getOptionLabel}
                isItemEqualToValue={(option, selectedOption) => option.value === selectedOption.value}
                onValueChange={(value) => onChange(value, getSelectorKey(filter))}
            >
                <ComboboxInput
                    id={`dropdown-${getSelectorKey(filter)}`}
                    aria-label={filter.label}
                    className="bg-paper"
                    placeholder={t('search')}
                    showClear={clearable}
                />
                <ComboboxContent className="shadow-dropdown">
                    <ComboboxEmpty>{t('no-options')}</ComboboxEmpty>
                    <ComboboxList>
                        {(option: SelectOption) => (
                            <ComboboxItem key={String(option.value)} value={option}>
                                {option.label}
                            </ComboboxItem>
                        )}
                    </ComboboxList>
                </ComboboxContent>
            </Combobox>
        </div>
    );
});
